import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../hooks/useCart';

/**
 * Botón "+" con stepper expandido (-, qty, +). Cuando hay 0 unidades del
 * producto en el carrito, muestra solo el "+" (36px). Al agregar al carrito
 * se expande mostrando "− qty +" (86px) con animación.
 *
 * Usado por BusinessCard y donde sea que se agregue un producto al carrito
 * desde una card.
 *
 * Props:
 *   - product: objeto del producto (debe tener _id o id)
 *   - businessId: opcional. Si se pasa, se mergea al producto antes de
 *     llamar addItem (algunos contextos como BusinessCard no traen el
 *     businessId en el product, otros ya lo traen).
 *   - className: posicionamiento del wrapper (ej. "absolute bottom-2 right-2").
 */
export default function AddToCartButton({ product, businessId, className = '' }) {
  const { quickAdd, getItem, incrementQuantity, decrementQuantity } = useCart();
  const productId = product._id || product.id;
  const cartItem = getItem(productId);
  const qty = cartItem?.quantity || 0;
  const expanded = qty > 0;

  const doAdd = (e) => {
    e.stopPropagation();
    const productWithBiz = businessId ? { ...product, businessId } : product;
    // Con extras abre la hoja para elegirlos; sin extras agrega directo.
    quickAdd(productWithBiz);
  };

  const doDec = (e) => {
    e.stopPropagation();
    if (qty === 1) {
      decrementQuantity(productId, 1, 0);
    } else {
      decrementQuantity(productId);
    }
  };

  return (
    <motion.div
      animate={{ width: expanded ? 86 : 36 }}
      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
      className={`h-9 rounded-full shadow-lg overflow-hidden ${className}`}
      style={{
        background: 'linear-gradient(to top, var(--color-product-btn-ring-from) 45%, var(--color-product-btn-ring-to))',
        padding: '2px'
      }}
      onClick={(e) => {
        if (!expanded) doAdd(e);
        else e.stopPropagation();
      }}
    >
      <div className={`flex items-center w-full h-full rounded-full bg-product-btn-bg px-1.5 ${expanded ? 'justify-between' : 'justify-center'}`}>
        <AnimatePresence mode="popLayout">
          {expanded && (
            <motion.button
              key="minus"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.2 }}
              onClick={doDec}
              className="flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
            >
              <svg width="12" height="3" viewBox="0 0 12 3" fill="none">
                <path d="M1.5 1.5H10.5" stroke="var(--color-product-btn-stroke)" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </motion.button>
          )}

          {expanded && (
            <motion.span
              key="qty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.1 }}
              style={{ color: 'var(--color-product-btn-stroke)' }}
              className="text-sm font-bold flex-1 text-center"
            >
              {qty}
            </motion.span>
          )}
        </AnimatePresence>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (expanded) incrementQuantity(productId);
            else doAdd(e);
          }}
          className="flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
        >
          {/* "+" de trazos rectos de la marca: el mismo en todas las tarjetas. */}
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.1807 0.939453C10.8488 1.33766 11.1699 2.1231 10.9678 2.86621L10.0234 6.33984H16.001L15.5098 8.06738C15.2975 8.81386 14.6062 9.33008 13.8193 9.33008H9.20996L7.39551 16L5.8291 15.0859C5.15206 14.6908 4.82465 13.8992 5.02832 13.1504L6.06738 9.33008H0L0.512695 7.58691C0.730175 6.84811 1.41743 6.33995 2.19824 6.33984H6.88086L8.60547 0L10.1807 0.939453Z"
              fill="var(--color-product-btn-stroke)"
            />
          </svg>
        </button>
      </div>
    </motion.div>
  );
}
